import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Gamepad2, Brain, Trophy, Clock, Calendar,
  Play, X, AlertTriangle, Lock, Medal, Star,
  BookOpen, Search, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

// New AI-Ready Offline Games Configuration
const GAMES = [
  {
    id: 'ai-context-word-finder',
    name: 'Context Word Finder',
    description: 'Find missing words using context clues - 4 difficulty levels',
    icon: Search,
    color: 'cyan',
    bgColor: 'bg-cyan-500/10',
    iconColor: 'text-cyan-400',
    path: '/games/ai-context-word-finder/index.html'
  },
  {
    id: 'ai-picture-match',
    name: 'Picture Match',
    description: 'Match pictures with words - Visual learning',
    icon: Brain,
    color: 'emerald',
    bgColor: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
    path: '/games/ai-picture-match/index.html'
  },
  {
    id: 'ai-spelling-bee',
    name: 'Spelling Bee',
    description: 'Listen and spell - Progressive difficulty levels',
    icon: BookOpen,
    color: 'amber',
    bgColor: 'bg-amber-500/10',
    iconColor: 'text-amber-400',
    path: '/games/ai-spelling-bee/index.html'
  },
  {
    id: 'ai-sentence-builder',
    name: 'Sentence Builder',
    description: 'Arrange words to form sentences - Grammar practice',
    icon: MessageSquare,
    color: 'purple',
    bgColor: 'bg-purple-500/10',
    iconColor: 'text-purple-400',
    path: '/games/ai-sentence-builder/index.html'
  }
];

const StudentFunLearningPage = () => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [canSaveScore, setCanSaveScore] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedGameFilter, setSelectedGameFilter] = useState('all');
  // Check access
  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await api.get('/fun-learning/check-access');
      setHasAccess(res.data.has_access);
      setCanSaveScore(res.data.can_save_score);
      setAccessMessage(res.data.message);
      if (res.data.has_access) {
        fetchLeaderboard();
      }
    } catch (err) {
      setHasAccess(false);
      setAccessMessage('Failed to check access');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const params = selectedGameFilter !== 'all' ? `?game=${selectedGameFilter}` : '';
      const res = await api.get(`/fun-learning/scores${params}`);
      setLeaderboard(res.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchLeaderboard();
    }
  }, [selectedGameFilter, hasAccess]);

  // Handle postMessage from games
  const handleGameMessage = useCallback(async (event) => {
    // Verify origin if needed
    if (event.data && event.data.type === 'GAME_SCORE') {
      const { gameId, score } = event.data;
      // Map gameId to display name
      const game = GAMES.find(g => g.id === gameId);
      const gameName = game ? game.name : gameId;
      
      if (canSaveScore) {
        try {
          await api.post('/fun-learning/scores', {
            game_name: gameName,
            score: score,
            time_taken: 0
          });
          toast.success(`Score saved: ${score} points!`);
          fetchLeaderboard();
        } catch (err) {
          toast.error('Failed to save score');
        }
      } else {
        toast.info(`Play mode: Score ${score} (not saved)`);
      }
    } else if (event.data && event.data.type === 'GAME_CLOSE') {
      setActiveGame(null);
    }
  }, [canSaveScore]);

  useEffect(() => {
    window.addEventListener('message', handleGameMessage);
    return () => window.removeEventListener('message', handleGameMessage);
  }, [handleGameMessage]);

  // ESC to close game when not in browser fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeGame && e.key === 'Escape' && !document.fullscreenElement) {
        closeGame();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeGame]);

  const openGame = async (game) => {
    setActiveGame(game);
    try {
      await document.documentElement.requestFullscreen();
    } catch (err) {
      // Fullscreen not supported or denied — game still works in overlay mode
    }
  };

  const closeGame = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setActiveGame(null);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Medal className="h-5 w-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-300" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-slate-400 font-medium">{rank}</span>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Access denied view
  if (!hasAccess) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="max-w-md w-full text-center">
            <CardContent className="pt-8 pb-8">
              <div className="mx-auto mb-4 p-4 rounded-full bg-amber-500/20 w-fit">
                <Lock className="h-8 w-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                Access Restricted
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {accessMessage}
              </p>
              <p className="text-sm text-slate-400">
                Please contact your teacher or administrator for access.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Game fullscreen view - Only show ONE active game at a time
  if (activeGame) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
        {/* Single Game Header - Only one at a time */}
        <div className="flex items-center justify-between p-3 bg-slate-800 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <activeGame.icon className={`h-5 w-5 ${activeGame.iconColor}`} />
            <span className="text-white font-semibold text-sm">{activeGame.name}</span>
            {!canSaveScore && (
              <Badge variant="secondary" className="bg-amber-500/20 text-amber-300 text-xs">
                Play Mode
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={closeGame}
              className="text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <X className="h-4 w-4 mr-1" />
              Close Game
            </Button>
          </div>
        </div>
        
        {/* Game iframe - Takes full remaining space */}
        <div className="flex-1 overflow-hidden">
          <iframe
            src={activeGame.path}
            className="w-full h-full border-0"
            title={activeGame.name}
            allow="fullscreen"
            style={{ display: 'block' }}
          />
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <Gamepad2 className="h-8 w-8 text-violet-500" />
              Fun Learning Games
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Learn English through interactive games
            </p>
          </div>
        </div>

        {/* Game Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {GAMES.map((game) => (
            <Card
              key={game.id}
              className={`group cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-${game.color}-500/50 ${game.bgColor}`}
              onClick={() => openGame(game)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-lg ${game.bgColor} group-hover:scale-110 transition-transform`}>
                    <game.icon className={`h-6 w-6 ${game.iconColor}`} />
                  </div>
                  <Button
                    size="sm"
                    className={`bg-${game.color}-500 hover:bg-${game.color}-600 text-white`}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Play
                  </Button>
                </div>
                <CardTitle className="text-lg mt-3">{game.name}</CardTitle>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {game.description}
                </p>
              </CardHeader>
              <CardContent>
                {/* Tags removed as per user request */}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-400" />
                <CardTitle>Leaderboard</CardTitle>
              </div>
              <Select value={selectedGameFilter} onValueChange={setSelectedGameFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Games" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Games</SelectItem>
                  {GAMES.map(game => (
                    <SelectItem key={game.id} value={game.name}>
                      {game.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Trophy className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No scores yet. Play games to appear on the leaderboard!</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {leaderboard.map((entry, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-8 flex items-center justify-center">
                          {getRankBadge(index + 1)}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900 dark:text-white">
                            {entry.student_name}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {entry.game_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Star className="h-4 w-4 text-amber-400" />
                          <p className="text-lg font-bold text-slate-900 dark:text-white">
                            {entry.score}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-400 justify-end mt-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(entry.played_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudentFunLearningPage;